/**
 * Glisser au pointeur, fait maison (Pointer Events, aucune dépendance ; ADR-146 addendum (13)) : un appui
 * ne devient un glisser qu'au-delà d'un seuil, pour qu'un clic reste un clic ; Échap annule ; le clic qui
 * suit un dépôt est avalé (il ne doit ni sélectionner ni insérer une seconde fois).
 */

export interface PointerDragHandlers {
  /** Le seuil est franchi : le glisser commence. */
  onStart: (x: number, y: number) => void;
  onMove: (x: number, y: number) => void;
  onDrop: (x: number, y: number) => void;
  onCancel: () => void;
}

export const DRAG_THRESHOLD = 5;

/** Avale le prochain clic (capturé sur la fenêtre), puis se retire. */
export function swallowNextClick(): void {
  if (typeof window === 'undefined') return;
  const swallow = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    window.removeEventListener('click', swallow, true);
  };
  window.addEventListener('click', swallow, true);
  // Un dépôt hors de toute cible ne produit pas toujours de clic : le piège ne reste pas armé.
  window.setTimeout(() => window.removeEventListener('click', swallow, true), 0);
}

/**
 * Arme un glisser à partir d'un appui (bouton principal) ; rend de quoi le désarmer. Rien ne se passe tant
 * que le pointeur n'a pas bougé de `threshold` pixels.
 */
export function armPointerDrag(start: { clientX: number; clientY: number; pointerId?: number; button?: number }, handlers: PointerDragHandlers, threshold = DRAG_THRESHOLD): () => void {
  if (typeof window === 'undefined' || (start.button !== undefined && start.button !== 0)) return () => undefined;
  let dragging = false;
  const same = (event: PointerEvent): boolean => start.pointerId === undefined || event.pointerId === undefined || event.pointerId === start.pointerId;
  const dispose = (): void => {
    window.removeEventListener('pointermove', onMove, true);
    window.removeEventListener('pointerup', onUp, true);
    window.removeEventListener('pointercancel', onCancel, true);
    window.removeEventListener('keydown', onKey, true);
  };
  const onMove = (event: PointerEvent): void => {
    if (!same(event)) return;
    if (!dragging) {
      if (Math.hypot(event.clientX - start.clientX, event.clientY - start.clientY) < threshold) return;
      dragging = true;
      handlers.onStart(event.clientX, event.clientY);
    }
    event.preventDefault();
    handlers.onMove(event.clientX, event.clientY);
  };
  const onUp = (event: PointerEvent): void => {
    if (!same(event)) return;
    dispose();
    if (!dragging) return;
    // Le relâchement d'un dépôt n'atteint personne d'autre : sans cela, l'écouteur du canevas le lirait
    // comme un clic et choisirait l'élément sous le pointeur au lieu de l'élément déposé (React a déjà
    // rendu entre les deux écouteurs, au point de contrôle des microtâches du navigateur).
    event.stopPropagation();
    swallowNextClick();
    handlers.onDrop(event.clientX, event.clientY);
  };
  const onCancel = (): void => {
    dispose();
    if (dragging) handlers.onCancel();
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !dragging) return;
    event.preventDefault();
    event.stopPropagation();
    dispose();
    handlers.onCancel();
  };
  window.addEventListener('pointermove', onMove, true);
  window.addEventListener('pointerup', onUp, true);
  window.addEventListener('pointercancel', onCancel, true);
  window.addEventListener('keydown', onKey, true);
  return dispose;
}
